
import { useState, useEffect, useRef } from 'react';
import { Renderer, Triangle, Program, Mesh } from 'ogl';

// Prism Component
const Prism = ({
  height = 3.5,
  baseWidth = 5.5,
  animationType = 'rotate',
  glow = 1,
  offset = { x: 0, y: 0 },
  noise = 0.5,
  transparent = true,
  scale = 3.6,
  hueShift = 0,
  colorFrequency = 1,
  hoverStrength = 2,
  inertia = 0.05,
  bloom = 1,
  suspendWhenOffscreen = false,
  timeScale = 0.5
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const H = Math.max(0.001, height);
    const BW = Math.max(0.001, baseWidth);
    const BASE_HALF = BW * 0.5;
    const GLOW = Math.max(0.0, glow);
    const NOISE = Math.max(0.0, noise);
    const offX = offset?.x ?? 0;
    const offY = offset?.y ?? 0;
    const SAT = transparent ? 1.5 : 1;
    const SCALE = Math.max(0.001, scale);
    const HUE = hueShift || 0;
    const CFREQ = Math.max(0.0, colorFrequency || 1);
    const BLOOM = Math.max(0.0, bloom || 1);
    const RSX = 1;
    const RSY = 1;
    const RSZ = 1;
    const TS = Math.max(0, timeScale || 1);
    const HOVSTR = Math.max(0, hoverStrength || 1);
    const INERT = Math.max(0, Math.min(1, inertia || 0.12));

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const renderer = new Renderer({
      dpr,
      alpha: transparent,
      antialias: false
    });
    const gl = renderer.gl;
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.disable(gl.BLEND);

    Object.assign(gl.canvas.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      display: 'block'
    });
    container.appendChild(gl.canvas);

    const vertex = /* glsl */ `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragment = /* glsl */ `
      precision highp float;

      uniform vec2  iResolution;
      uniform float iTime;

      uniform float uHeight;
      uniform float uBaseHalf;
      uniform mat3  uRot;
      uniform int   uUseBaseWobble;
      uniform float uGlow;
      uniform vec2  uOffsetPx;
      uniform float uNoise;
      uniform float uSaturation;
      uniform float uScale;
      uniform float uHueShift;
      uniform float uColorFreq;
      uniform float uBloom;
      uniform float uCenterShift;
      uniform float uInvBaseHalf;
      uniform float uInvHeight;
      uniform float uMinAxis;
      uniform float uPxScale;
      uniform float uTimeScale;

      vec4 tanh4(vec4 x){
        vec4 e2x = exp(2.0*x);
        return (e2x - 1.0) / (e2x + 1.0);
      }

      float rand(vec2 co){
        return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453123);
      }

      float sdOctaAnisoInv(vec3 p){
        vec3 q = vec3(abs(p.x) * uInvBaseHalf, abs(p.y) * uInvHeight, abs(p.z) * uInvBaseHalf);
        float m = q.x + q.y + q.z - 1.0;
        return m * uMinAxis * 0.5773502691896258;
      }

      float sdPyramidUpInv(vec3 p){
        float oct = sdOctaAnisoInv(p);
        float halfSpace = -p.y;
        return max(oct, halfSpace);
      }

      mat3 hueRotation(float a){
        float c = cos(a), s = sin(a);
        mat3 W = mat3(
          0.299, 0.587, 0.114,
          0.299, 0.587, 0.114,
          0.299, 0.587, 0.114
        );
        mat3 U = mat3(
           0.701, -0.587, -0.114,
          -0.299,  0.413, -0.114,
          -0.300, -0.588,  0.886
        );
        mat3 V = mat3(
           0.168, -0.331,  0.500,
           0.328,  0.035, -0.500,
          -0.497,  0.296,  0.201
        );
        return W + U * c + V * s;
      }

      void main(){
        vec2 f = (gl_FragCoord.xy - 0.5 * iResolution.xy - uOffsetPx) * uPxScale;

        float z = 5.0;
        float d = 0.0;

        vec3 p;
        vec4 o = vec4(0.0);

        float centerShift = uCenterShift;
        float cf = uColorFreq;

        mat2 wob = mat2(1.0);
        if (uUseBaseWobble == 1) {
          float t = iTime * uTimeScale;
          float c0 = cos(t + 0.0);
          float c1 = cos(t + 33.0);
          float c2 = cos(t + 11.0);
          wob = mat2(c0, c1, c2, c0);
        }

        const int STEPS = 100;
        for (int i = 0; i < STEPS; i++) {
          p = vec3(f, z);
          p.xz = p.xz * wob;
          p = uRot * p;
          vec3 q = p;
          q.y += centerShift;
          d = 0.1 + 0.2 * abs(sdPyramidUpInv(q));
          z -= d;
          o += (sin((p.y + z) * cf + vec4(0.0, 1.0, 2.0, 3.0)) + 1.0) / d;
        }

        o = tanh4(o * o * (uGlow * uBloom) / 1e5);

        vec3 col = o.rgb;
        float n = rand(gl_FragCoord.xy + vec2(iTime));
        col += (n - 0.5) * uNoise;
        col = clamp(col, 0.0, 1.0);

        float L = dot(col, vec3(0.2126, 0.7152, 0.0722));
        col = clamp(mix(vec3(L), col, uSaturation), 0.0, 1.0);

        if(abs(uHueShift) > 0.0001){
          col = clamp(hueRotation(uHueShift) * col, 0.0, 1.0);
        }

        gl_FragColor = vec4(col, o.a);
      }
    `;

    const geometry = new Triangle(gl);
    const iResBuf = new Float32Array(2);
    const offsetPxBuf = new Float32Array(2);

    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        iResolution: { value: iResBuf },
        iTime: { value: 0 },
        uHeight: { value: H },
        uBaseHalf: { value: BASE_HALF },
        uUseBaseWobble: { value: 1 },
        uRot: { value: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]) },
        uGlow: { value: GLOW },
        uOffsetPx: { value: offsetPxBuf },
        uNoise: { value: NOISE },
        uSaturation: { value: SAT },
        uScale: { value: SCALE },
        uHueShift: { value: HUE },
        uColorFreq: { value: CFREQ },
        uBloom: { value: BLOOM },
        uCenterShift: { value: H * 0.25 },
        uInvBaseHalf: { value: 1 / BASE_HALF },
        uInvHeight: { value: 1 / H },
        uMinAxis: { value: Math.min(BASE_HALF, H) },
        uPxScale: {
          value: 1 / ((gl.drawingBufferHeight || 1) * 0.1 * SCALE)
        },
        uTimeScale: { value: TS }
      }
    });
    const mesh = new Mesh(gl, { geometry, program });

    const resize = () => {
      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;
      renderer.setSize(w, h);
      iResBuf[0] = gl.drawingBufferWidth;
      iResBuf[1] = gl.drawingBufferHeight;
      offsetPxBuf[0] = offX * dpr;
      offsetPxBuf[1] = offY * dpr;
      program.uniforms.uPxScale.value = 1 / ((gl.drawingBufferHeight || 1) * 0.1 * SCALE);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    const rotBuf = new Float32Array(9);
    const setMat3FromEuler = (yawY, pitchX, rollZ, out) => {
      const cy = Math.cos(yawY),
        sy = Math.sin(yawY);
      const cx = Math.cos(pitchX),
        sx = Math.sin(pitchX);
      const cz = Math.cos(rollZ),
        sz = Math.sin(rollZ);
      const r00 = cy * cz + sy * sx * sz;
      const r01 = -cy * sz + sy * sx * cz;
      const r02 = sy * cx;

      const r10 = cx * sz;
      const r11 = cx * cz;
      const r12 = -sx;

      const r20 = -sy * cz + cy * sx * sz;
      const r21 = sy * sz + cy * sx * cz;
      const r22 = cy * cx;

      out[0] = r00;
      out[1] = r10;
      out[2] = r20;
      out[3] = r01;
      out[4] = r11;
      out[5] = r21;
      out[6] = r02;
      out[7] = r12;
      out[8] = r22;
      return out;
    };

    const NOISE_IS_ZERO = NOISE < 1e-6;
    let raf = 0;
    const t0 = performance.now();
    const startRAF = () => {
      if (raf) return;
      raf = requestAnimationFrame(render);
    };
    const stopRAF = () => {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const rnd = () => Math.random();
    const wX = (0.3 + rnd() * 0.6) * RSX;
    const wY = (0.2 + rnd() * 0.7) * RSY;
    const wZ = (0.1 + rnd() * 0.5) * RSZ;
    const phX = rnd() * Math.PI * 2;
    const phZ = rnd() * Math.PI * 2;

    let yaw = 0,
      pitch = 0,
      roll = 0;
    let targetYaw = 0,
      targetPitch = 0;
    const lerp = (a, b, t) => a + (b - a) * t;

    const pointer = { x: 0, y: 0, inside: true };
    const onMove = e => {
      const ww = Math.max(1, window.innerWidth);
      const wh = Math.max(1, window.innerHeight);
      const cx = ww * 0.5;
      const cy = wh * 0.5;
      const nx = (e.clientX - cx) / (ww * 0.5);
      const ny = (e.clientY - cy) / (wh * 0.5);
      pointer.x = Math.max(-1, Math.min(1, nx));
      pointer.y = Math.max(-1, Math.min(1, ny));
      pointer.inside = true;
    };
    const onLeave = () => {
      pointer.inside = false;
    };
    const onBlur = () => {
      pointer.inside = false;
    };

    let onPointerMove = null;
    if (animationType === 'hover') {
      onPointerMove = e => {
        onMove(e);
        startRAF();
      };
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('mouseleave', onLeave);
      window.addEventListener('blur', onBlur);
      program.uniforms.uUseBaseWobble.value = 0;
    } else if (animationType === '3drotate') {
      program.uniforms.uUseBaseWobble.value = 0;
    } else {
      program.uniforms.uUseBaseWobble.value = 1;
    }

    const render = t => {
      const time = (t - t0) * 0.001;
      program.uniforms.iTime.value = time;

      let continueRAF = true;

      if (animationType === 'hover') {
        const maxPitch = 0.6 * HOVSTR;
        const maxYaw = 0.6 * HOVSTR;
        targetYaw = (pointer.inside ? -pointer.x : 0) * maxYaw;
        targetPitch = (pointer.inside ? pointer.y : 0) * maxPitch;
        const prevYaw = yaw;
        const prevPitch = pitch;
        const prevRoll = roll;
        yaw = lerp(prevYaw, targetYaw, INERT);
        pitch = lerp(prevPitch, targetPitch, INERT);
        roll = lerp(prevRoll, 0, 0.1);
        program.uniforms.uRot.value = setMat3FromEuler(yaw, pitch, roll, rotBuf);

        if (NOISE_IS_ZERO) {
          const settled =
            Math.abs(yaw - targetYaw) < 1e-4 && Math.abs(pitch - targetPitch) < 1e-4 && Math.abs(roll) < 1e-4;
          if (settled) continueRAF = false;
        }
      } else if (animationType === '3drotate') {
        const tScaled = time * TS;
        yaw = tScaled * wY;
        pitch = Math.sin(tScaled * wX + phX) * 0.6;
        roll = Math.sin(tScaled * wZ + phZ) * 0.5;
        program.uniforms.uRot.value = setMat3FromEuler(yaw, pitch, roll, rotBuf);
        if (TS < 1e-6) continueRAF = false;
      } else {
        rotBuf[0] = 1;
        rotBuf[1] = 0;
        rotBuf[2] = 0;
        rotBuf[3] = 0;
        rotBuf[4] = 1;
        rotBuf[5] = 0;
        rotBuf[6] = 0;
        rotBuf[7] = 0;
        rotBuf[8] = 1;
        program.uniforms.uRot.value = rotBuf;
        if (TS < 1e-6) continueRAF = false;
      }

      renderer.render({ scene: mesh });
      if (continueRAF) {
        raf = requestAnimationFrame(render);
      } else {
        raf = 0;
      }
    };

    if (suspendWhenOffscreen) {
      const io = new IntersectionObserver(entries => {
        const vis = entries.some(e => e.isIntersecting);
        if (vis) startRAF();
        else stopRAF();
      });
      io.observe(container);
      startRAF();
      container.__prismIO = io;
    } else {
      startRAF();
    }

    return () => {
      stopRAF();
      ro.disconnect();
      if (animationType === 'hover') {
        if (onPointerMove) window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('mouseleave', onLeave);
        window.removeEventListener('blur', onBlur);
      }
      if (suspendWhenOffscreen) {
        const io = container.__prismIO;
        if (io) io.disconnect();
        delete container.__prismIO;
      }
      if (gl.canvas.parentElement === container) container.removeChild(gl.canvas);
    };
  }, [
    height,
    baseWidth,
    animationType,
    glow,
    noise,
    offset?.x,
    offset?.y,
    scale,
    transparent,
    hueShift,
    colorFrequency,
    timeScale,
    hoverStrength,
    inertia,
    bloom,
    suspendWhenOffscreen
  ]);

  return <div className="absolute inset-0 w-full h-full" ref={containerRef} />;
};

// Animation Hook
const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return visibleElements;
};

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const visibleElements = useScrollAnimation();

  const getAnimationClass = (elementId, baseClass = '') => {
    const isVisible = visibleElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${
      isVisible
        ? 'opacity-100 translate-y-0 scale-100'
        : 'opacity-0 translate-y-8 scale-95'
    }`;
  };

  const handleProjectClick = project => {
    setExpandedProject(expandedProject === project.id ? null : project.id);
  };

  const handleFAQClick = faqId => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
  };

  const projects = [
    {
      id: 1,
      year: '2023',
      title: 'RetailAI - Predictive inventory system',
      description: 'Advanced machine learning system that predicts inventory needs with 95% accuracy.',
      technologies: ['TensorFlow', 'Python', 'AWS', 'PostgreSQL'],
      results: ['40% reduction in stockouts', '35% decrease in overstock', '95% prediction accuracy', '$2M annual savings'],
      image: 'https://public.readdy.ai/ai/img_res/a3ffb5f81f3d86bc173958a73f7ed49d.jpg'
    },
    {
      id: 2,
      year: '2024',
      title: 'HealthAI - Medical diagnosis assistant',
      description: 'AI-powered diagnostic tool that assists medical professionals with 98% accuracy.',
      technologies: ['PyTorch', 'Computer Vision', 'DICOM', 'React'],
      results: ['98% diagnostic accuracy', '60% faster diagnosis', '500+ hospitals using', 'FDA approved'],
      image: 'https://readdy.ai/api/search-image?query=Medical%20AI%20diagnostic%20interface%20showing%20X-ray%20analysis%2C%20clean%20hospital%20environment%2C%20doctor%20using%20advanced%20medical%20technology%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20healthcare%20setting%20with%20digital%20displays&width=600&height=400&seq=health-ai-2&orientation=landscape'
    },
    {
      id: 3,
      year: '2024',
      title: 'FinanceAI - Fraud detection engine',
      description: 'Real-time fraud detection system processing millions of transactions daily.',
      technologies: ['Keras', 'Apache Kafka', 'Redis', 'Node.js'],
      results: ['99.7% fraud detection rate', '0.1% false positives', '$50M fraud prevented', '24/7 monitoring'],
      image: 'https://readdy.ai/api/search-image?query=Financial%20security%20dashboard%20with%20fraud%20detection%20analytics%2C%20dark%20theme%20interface%20showing%20transaction%20monitoring%2C%20cybersecurity%20visualization%2C%20green%20and%20red%20data%20indicators%2C%20modern%20fintech%20environment&width=600&height=400&seq=finance-ai-3&orientation=landscape'
    }
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Floating Navigation */}
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 hidden lg:block">
        <nav className="bg-transparent backdrop-blur-md border border-gray-800/50 rounded-full px-6 py-3">
          <div className="flex items-center justify-center space-x-8">
            <a href="#our-process" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap font-montserrat">
              AI Process
            </a>
            <a href="#services" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap font-montserrat">
              AI Solutions
            </a>
            <a href="#work" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap font-montserrat">
              Case Studies
            </a>
            <a href="#testimonials" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap font-montserrat">
              Client Reviews
            </a>
            <a href="#contact" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm flex items-center whitespace-nowrap font-montserrat">
              Get Started
              <i className="ri-arrow-right-up-line ml-1 text-xs"></i>
            </a>
            <button className="bg-green-200 text-black px-4 py-2 rounded-full font-semibold hover:bg-green-100 transition-colors whitespace-nowrap cursor-pointer text-sm font-montserrat">
              Free Consultation
            </button>
          </div>
        </nav>
      </div>
      {/* Header */}
      <header className="relative min-h-screen flex flex-col">
        {/* Hero Section */}
        <div className="flex-1 flex items-center justify-center px-6 relative">
          {/* Prism Background */}
          <Prism
            animationType="3drotate"
            glow={1}
            scale={3.6}
            hueShift={0}
            colorFrequency={1}
            timeScale={0.5}
            transparent={true}
            bloom={1}
            hoverStrength={2}
            inertia={0.05}
            noise={0}
          />

          <div className="text-center max-w-5xl mx-auto relative z-10 animate-fade-in-up">
            <h1 className="text-5xl lg:text-7xl font-normal leading-tight mb-8 font-montserrat">
              Revolutionizing <span className="text-green-200">business</span> with <span className="text-green-200">AI products</span>.
            </h1>

            <p className="text-lg lg:text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed font-montserrat">
              We develop cutting-edge AI products and intelligent solutions that transform how businesses operate and compete.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-transparent border border-gray-600 text-white px-8 py-3 rounded-full font-semibold hover:border-green-200 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all duration-300 whitespace-nowrap cursor-pointer font-montserrat"
              >
                Our services
              </button>
              <button
                onClick={scrollToContact}
                className="bg-transparent border border-gray-600 text-white px-8 py-3 rounded-full font-semibold hover:border-green-200 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center font-montserrat"
              >
                Get in touch
                <i className="ri-arrow-right-up-line ml-2 text-sm"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-green-200/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-100/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-green-100/20 rounded-full blur-2xl"></div>
        </div>
      </header>

      {/* About Us Section */}
      <section 
        id="about" 
        data-animate 
        className={getAnimationClass('about', 'px-6 py-20 lg:px-12 relative')}
      >
        {/* Background Elements for seamless transition */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/3 w-80 h-80 bg-green-200/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-green-100/8 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-green-100/10 rounded-full blur-2xl"></div>
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="text-4xl lg:text-6xl font-normal leading-tight mb-8">
            <div className="mb-4">
              We're a <span className="text-green-200">leading</span> <span className="text-green-200">AI product</span>
            </div>
            <div className="mb-4">
              <span className="text-green-200">development company</span> 
              <span className="inline-block ml-4 text-4xl lg:text-6xl">🤖</span>
            </div>
            <div>
              We transform businesses with <span className="text-green-200">intelligent AI</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-green-200">product solutions</span>
              <span className="inline-block ml-4 text-4xl lg:text-6xl">⚡</span>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        id="our-process" 
        data-animate 
        className={getAnimationClass('our-process', 'px-6 py-20 lg:px-12')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-6xl lg:text-7xl mb-4">Our <span className="text-green-200">process</span></h2>
          </div>

          {/* First Row - 3 Cards */}
          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Step 1 */}
            <div 
              id="process-step-1" 
              data-animate 
              className={getAnimationClass('process-step-1', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.1s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg mb-6 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-200/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-checkbox-circle-line text-green-200 text-2xl"></i>
                    </div>
                    <p className="text-gray-400 text-sm">AI Product Plans</p>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4"><span className="text-green-200">01.</span> Choose AI Solution</h3>
              <p className="text-gray-400 leading-relaxed">
                Select from our range of AI products including machine learning models, natural language processing tools, and computer vision solutions tailored to your business needs.
              </p>
            </div>

            {/* Step 2 */}
            <div 
              id="process-step-2" 
              data-animate 
              className={getAnimationClass('process-step-2', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.2s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg mb-6 flex items-center justify-center">
                  <div className="grid grid-cols-4 gap-2">
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/RqlD623htlZjBOW5TGIBeTCn9Rw.png" alt="TensorFlow" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/h8QmijZdYgf0r2qTm0cl7NQHkJ4.png" alt="PyTorch" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/DbqxOOL7wdsfuIM8R12g6g9TeU.png" alt="OpenAI" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/liyIkoI7O34iCSvLdx9KxCC5cBs.png" alt="Hugging Face" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/eIDifEepcllMTSnFfdMm68QyzI.png" alt="Google AI" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/tgAdHwxLRfm8Xt4ZgpJr2sZaPxY.png" alt="Azure AI" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/HckOd2xJ67RzaG2caZs2ur1UHA.png" alt="AWS AI" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                      <img src="https://framerusercontent.com/images/jgI8KEzSaG810pJKQsKIMoGc.webp" alt="Anthropic" className="w-6 h-6 object-contain" />
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4"><span className="text-green-200">02.</span> Request AI Features</h3>
              <p className="text-gray-400 leading-relaxed">
                Define your AI requirements including data processing capabilities, model training specifications, and integration needs. Our AI experts will customize solutions for your specific use case.
              </p>
            </div>

            {/* Step 3 */}
            <div 
              id="process-step-3" 
              data-animate 
              className={getAnimationClass('process-step-3', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.3s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg mb-6 flex items-center justify-center">
                  <div className="w-16 h-16 bg-green-200/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-brain-line text-green-200 text-2xl"></i>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4"><span className="text-green-200">03.</span> Build AI Models</h3>
              <p className="text-gray-400 leading-relaxed">
                Our AI engineers develop and train custom machine learning models, neural networks, and AI algorithms optimized for your data and business objectives.
              </p>
            </div>
          </div>

          {/* Second Row - 2 Cards */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Step 4 */}
            <div 
              id="process-step-4" 
              data-animate 
              className={getAnimationClass('process-step-4', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.4s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg flex items-center justify-center">
                  <div className="flex space-x-6">
                    <div className="text-center">
                      <div className="text-sm text-gray-400 mb-2">Accuracy</div>
                      <div className="w-24 h-2 bg-green-200 rounded-full">
                        <div className="w-20 h-2 bg-green-200 rounded-full"></div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-400 mb-2">Performance</div>
                      <div className="w-24 h-2 bg-green-200 rounded-full">
                        <div className="w-16 h-2 bg-green-200 rounded-full"></div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-400 mb-2">Reliability</div>
                      <div className="w-24 h-2 bg-green-200 rounded-full">
                        <div className="w-20 h-2 bg-green-200 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4"><span className="text-green-200">04.</span> Test & Optimize AI</h3>
              <p className="text-gray-400 leading-relaxed">
                Rigorous testing of AI models with real-world data, performance optimization, and fine-tuning to ensure maximum accuracy and efficiency for your specific requirements.
              </p>
            </div>

            {/* Step 5 */}
            <div 
              id="process-step-5" 
              data-animate 
              className={getAnimationClass('process-step-5', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.5s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg flex items-center justify-center">
                  <div className="w-16 h-16 bg-green-200/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-rocket-line text-green-200 text-2xl"></i>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4"><span className="text-green-200">05.</span> Deploy AI Solutions</h3>
              <p className="text-gray-400 leading-relaxed">
                Seamless deployment of AI products into your existing infrastructure with ongoing support, monitoring, and continuous improvement to maximize business impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="services" 
        data-animate 
        className={getAnimationClass('services', 'px-6 py-20 lg:px-12')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-6xl lg:text-7xl mb-4">Our AI <span className="text-green-200">products</span></h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div 
              id="service-1" 
              data-animate 
              className={getAnimationClass('service-1', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.1s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg mb-6 flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    <div className="w-12 h-8 bg-gray-700 rounded flex items-center justify-center">
                      <i className="ri-brain-line text-green-200 text-lg"></i>
                    </div>
                    <i className="ri-add-line text-gray-400"></i>
                    <div className="w-12 h-8 bg-gray-700 rounded flex items-center justify-center">
                      <i className="ri-database-line text-green-200 text-lg"></i>
                    </div>
                    <i className="ri-add-line text-gray-400"></i>
                    <div className="w-12 h-8 bg-gray-700 rounded flex items-center justify-center">
                      <i className="ri-line-chart-line text-green-200 text-lg"></i>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Machine Learning Models</h3>
              <p className="text-gray-400 leading-relaxed">
                Custom ML models for predictive analytics, classification, regression, and pattern recognition. Boost decision-making with data-driven AI insights.
              </p>
            </div>

            <div 
              id="service-2" 
              data-animate 
              className={getAnimationClass('service-2', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.2s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg mb-6 flex items-center justify-center">
                  <div className="w-full max-xs">
                    <div className="bg-gray-700 rounded-lg p-3 mb-2">
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 bg-green-200 rounded-full mr-2"></div>
                        <span className="text-xs text-gray-300">User Query</span>
                      </div>
                      <p className="text-xs text-gray-400">Analyze customer sentiment</p>
                    </div>
                    <div className="bg-gray-600 rounded-lg p-3">
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 bg-blue-500 rounded-full mr-2 flex items-center justify-center">
                          <span className="text-xs text-white">AI</span>
                        </div>
                        <span className="text-xs text-gray-300">NLP Engine</span>
                      </div>
                      <p className="text-xs text-gray-400">Sentiment: 85% Positive</p>
                      <p className="text-xs text-gray-500 mt-1">Key themes: satisfaction, quality, service excellence...</p>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Natural Language Processing</h3>
              <p className="text-gray-400 leading-relaxed">
                Advanced NLP solutions for text analysis, sentiment analysis, language translation, and intelligent document processing to understand human language.
              </p>
            </div>

            <div 
              id="service-3" 
              data-animate 
              className={getAnimationClass('service-3', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800 hover:border-gray-700 transition-colors')}
              style={{ animationDelay: '0.3s' }}
            >
              <div className="mb-6">
                <div className="w-full h-48 bg-gray-800/50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-200/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-eye-line text-green-200 text-2xl"></i>
                    </div>
                    <div className="w-32 h-16 bg-gray-700/50 rounded border-2 border-green-200/30 flex items-center justify-center">
                      <span className="text-xs text-green-200">Object Detected</span>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Computer Vision</h3>
              <p className="text-gray-400 leading-relaxed">
                AI-powered image and video analysis including object detection, facial recognition, quality inspection, and visual content understanding for automation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Work Section */}
      <section 
        id="work" 
        data-animate 
        className={getAnimationClass('work', 'px-6 py-20 lg:px-12')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-6xl lg:text-7xl mb-4">Our AI <span className="text-green-200">Projects</span></h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              {projects.map((project, index) => (
                <div 
                  key={project.id}
                  id={`work-item-${project.id}`}
                  data-animate 
                  className={getAnimationClass(`work-item-${project.id}`, `relative bg-gray-900/30 rounded-2xl p-6 border transition-all duration-500 cursor-pointer ${
                    expandedProject === project.id 
                      ? 'border-green-200/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                      : 'border-gray-800 hover:border-gray-700'
                  }`)}
                  style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                  onClick={() => handleProjectClick(project)}
                >
                  {/* Aurora border animation */}
                  {expandedProject === project.id && (
                    <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                      <div className="absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-r from-green-200/20 via-green-100/30 to-green-200/20 animate-pulse"></div>
                    </div>
                  )}
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">{project.year}</p>
                        <h3 className="text-lg font-semibold mb-2">{project.title}</h3>
                      </div>
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`text-green-200 text-xl transition-transform duration-300 ${
                          expandedProject === project.id 
                            ? 'ri-subtract-line rotate-0' 
                            : 'ri-arrow-right-up-line group-hover:translate-x-1 group-hover:-translate-y-1'
                        }`}></i>
                      </div>
                    </div>
                    
                    {/* Expanded content */}
                    <div className={`transition-all duration-500 ease-out overflow-hidden ${
                      expandedProject === project.id 
                        ? 'max-h-96 opacity-100 mt-4' 
                        : 'max-h-0 opacity-0'
                    }`}>
                      <div className="border-t border-gray-700/50 pt-4">
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div 
              id="work-showcase" 
              data-animate 
              className={getAnimationClass('work-showcase', 'bg-gray-900/30 rounded-2xl p-8 border border-gray-800')}
              style={{ animationDelay: '0.4s' }}
            >
              <div className="h-80 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20floating%20holographic%20sphere%20with%20neural%20network%20patterns%2C%20dark%20space%20background%2C%20soft%20green%20and%20blue%20ambient%20lighting%2C%20minimalist%20futuristic%20design%2C%20clean%20geometric%20form%20with%20subtle%20glow%20effects%2C%20modern%20technology%20visualization&width=600&height=400&seq=neural-sphere-2&orientation=landscape" 
                  alt="AI Neural Sphere" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        id="testimonials" 
        data-animate 
        className={getAnimationClass('testimonials', 'px-6 py-20 lg:px-12')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-6xl lg:text-7xl mb-4">What our <span className="text-green-200">clients</span> say</h2>
          </div>
          
          {/* Scrolling testimonials container */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll-horizontal space-x-8">
              {/* First set of testimonials */}
              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"Their AI solution increased our efficiency by 67%"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  The machine learning models they built transformed our data analysis capabilities completely!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20business%20executive%20headshot%2C%20confident%20middle-aged%20man%20with%20short%20dark%20hair%2C%20wearing%20dark%20business%20suit%2C%20clean%20corporate%20background%2C%20professional%20lighting%2C%20high%20quality%20portrait%20photography&width=100&height=100&seq=exec-1&orientation=squarish" alt="Michael Chen" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">Michael Chen</p>
                    <p className="text-gray-400 text-sm">CTO - DataTech Solutions</p>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"Their NLP system processes 10,000+ documents daily"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  The natural language processing solution revolutionized our document workflow automation!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20businesswoman%20headshot%2C%20confident%20woman%20with%20shoulder-length%20brown%20hair%2C%20wearing%20professional%20blazer%2C%20clean%20office%20background%2C%20natural%20lighting%2C%20executive%20portrait%20photography&width=100&height=100&seq=exec-2&orientation=squarish" alt="Emma Rodriguez" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">Emma Rodriguez</p>
                    <p className="text-gray-400 text-sm">Head of Innovation - SmartFlow Inc</p>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"Computer vision AI reduced our quality control costs by 40%"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  Their AI-powered visual inspection system is incredibly accurate and reliable for our manufacturing!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20male%20executive%20headshot%2C%20mature%20businessman%20with%20gray%20hair%20and%20beard%2C%20wearing%20navy%20blue%20suit%2C%20confident%20expression%2C%20corporate%20office%20background%2C%20professional%20portrait%20lighting&width=100&height=100&seq=exec-3&orientation=squarish" alt="David Thompson" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">David Thompson</p>
                    <p className="text-gray-400 text-sm">Operations Director - TechManufacture Pro</p>
                  </div>
                </div>
              </div>

              {/* Additional testimonials */}
              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"AI chatbot handles 95% of customer inquiries automatically"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  Their conversational AI transformed our customer service operations beyond expectations!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20female%20executive%20headshot%2C%20confident%20Asian%20businesswoman%20with%20black%20hair%20in%20professional%20updo%2C%20wearing%20white%20blazer%2C%20modern%20office%20background%2C%20professional%20portrait%20photography&width=100&height=100&seq=exec-4&orientation=squarish" alt="Sarah Kim" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">Sarah Kim</p>
                    <p className="text-gray-400 text-sm">Customer Success Director - ServiceHub</p>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"Predictive analytics increased our sales by 85%"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  The AI forecasting models help us make data-driven decisions with incredible accuracy!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20businessman%20headshot%2C%20confident%20African%20American%20executive%20with%20short%20beard%2C%20wearing%20charcoal%20gray%20suit%2C%20clean%20corporate%20background%20and%20professional%20studio%20lighting&width=100&height=100&seq=exec-5&orientation=squarish" alt="James Wilson" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">James Wilson</p>
                    <p className="text-gray-400 text-sm">VP of Sales - GrowthTech</p>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"AI security system detected 99.8% of threats"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  Their cybersecurity AI provides unmatched protection against advanced threats and attacks!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20female%20executive%20headshot%2C%20confident%20woman%20with%20blonde%20hair%20in%20professional%20style%2C%20wearing%20black%20blazer%2C%20corporate%20office%20background%2C%20professional%20business%20photography&width=100&height=100&seq=exec-8&orientation=squarish" alt="Amanda Foster" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">Amanda Foster</p>
                    <p className="text-gray-400 text-sm">CISO - SecureNet Systems</p>
                  </div>
                </div>
              </div>

              {/* Duplicate set for seamless loop */}
              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"Their AI solution increased our efficiency by 67%"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  The machine learning models they built transformed our data analysis capabilities completely!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20business%20executive%20headshot%2C%20confident%20middle-aged%20man%20with%20short%20dark%20hair%2C%20wearing%20dark%20business%20suit%2C%20clean%20corporate%20background%2C%20professional%20lighting%2C%20high%20quality%20portrait%20photography&width=100&height=100&seq=exec-1&orientation=squarish" alt="Michael Chen" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">Michael Chen</p>
                    <p className="text-gray-400 text-sm">CTO - DataTech Solutions</p>
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-80 bg-gray-900/30 rounded-2xl p-8 border border-gray-800 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">"Their NLP system processes 10,000+ documents daily"</h3>
                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  The natural language processing solution revolutionized our document workflow automation!
                </p>
                <div className="flex items-center mt-auto">
                  <div className="w-12 h-12 mr-4 rounded-full overflow-hidden flex-shrink-0">
                    <img src="https://readdy.ai/api/search-image?query=Professional%20businesswoman%20headshot%2C%20confident%20woman%20with%20shoulder-length%20brown%20hair%2C%20wearing%20professional%20blazer%2C%20clean%20office%20background%2C%20natural%20lighting%2C%20executive%20portrait%20photography&width=100&height=100&seq=exec-2&orientation=squarish" alt="Emma Rodriguez" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold">Emma Rodriguez</p>
                    <p className="text-gray-400 text-sm">Head of Innovation - SmartFlow Inc</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq" 
        data-animate 
        className={getAnimationClass('faq', 'px-6 py-20 lg:px-12')}
      >
        <div className="max-w-4xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-6xl lg:text-7xl mb-4">AI FAQ</h2>
          </div>
          <div className="space-y-4">
            <div 
              id="faq-item-1" 
              data-animate 
              className={getAnimationClass('faq-item-1', `bg-gray-900/30 rounded-2xl p-6 border transition-all duration-500 cursor-pointer ${
                expandedFAQ === 1 
                  ? 'border-green-200/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                  : 'border-gray-800 hover:border-gray-700'
              }`)}
              style={{ animationDelay: '0.1s' }}
              onClick={() => handleFAQClick(1)}
            >
              <button className="w-full flex items-center justify-between text-left">
                <span className="text-lg font-medium">What makes our AI products unique?</span>
                <i className={`text-green-200 text-xl transition-transform duration-300 ${
                  expandedFAQ === 1 
                    ? 'ri-subtract-line rotate-0' 
                    : 'ri-arrow-right-up-line'
                }`}></i>
              </button>
              <div className={`transition-all duration-500 ease-out overflow-hidden ${
                expandedFAQ === 1 
                  ? 'max-h-96 opacity-100 mt-4' 
                  : 'max-h-0 opacity-0'
              }`}>
                <div className="border-t border-gray-700/50 pt-4">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Our AI products combine cutting-edge machine learning algorithms with industry-specific customization. We focus on practical business applications with proven ROI, offering 24/7 support and continuous model optimization.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              id="faq-item-2" 
              data-animate 
              className={getAnimationClass('faq-item-2', `bg-gray-900/30 rounded-2xl p-6 border transition-all duration-500 cursor-pointer ${
                expandedFAQ === 2 
                  ? 'border-green-200/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                  : 'border-gray-800 hover:border-gray-700'
              }`)}
              style={{ animationDelay: '0.2s' }}
              onClick={() => handleFAQClick(2)}
            >
              <button className="w-full flex items-center justify-between text-left">
                <span className="text-lg font-medium">How many AI models can I request each month?</span>
                <i className={`text-green-200 text-xl transition-transform duration-300 ${
                  expandedFAQ === 2 
                    ? 'ri-subtract-line rotate-0' 
                    : 'ri-arrow-right-up-line'
                }`}></i>
              </button>
              <div className={`transition-all duration-500 ease-out overflow-hidden ${
                expandedFAQ === 2 
                  ? 'max-h-96 opacity-100 mt-4' 
                  : 'max-h-0 opacity-0'
              }`}>
                <div className="border-t border-gray-700/50 pt-4">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Our packages range from 3 custom AI models per month in the Starter plan to unlimited models in the Enterprise plan. Each model includes training, testing, and deployment support.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              id="faq-item-3" 
              data-animate 
              className={getAnimationClass('faq-item-3', `bg-gray-900/30 rounded-2xl p-6 border transition-all duration-500 cursor-pointer ${
                expandedFAQ === 3 
                  ? 'border-green-200/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                  : 'border-gray-800 hover:border-gray-700'
              }`)}
              style={{ animationDelay: '0.3s' }}
              onClick={() => handleFAQClick(3)}
            >
              <button className="w-full flex items-center justify-between text-left">
                <span className="text-lg font-medium">Can I customize AI models for my specific needs?</span>
                <i className={`text-green-200 text-xl transition-transform duration-300 ${
                  expandedFAQ === 3 
                    ? 'ri-subtract-line rotate-0' 
                    : 'ri-arrow-right-up-line'
                }`}></i>
              </button>
              <div className={`transition-all duration-500 ease-out overflow-hidden ${
                expandedFAQ === 3 
                  ? 'max-h-96 opacity-100 mt-4' 
                  : 'max-h-0 opacity-0'
              }`}>
                <div className="border-t border-gray-700/50 pt-4">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Absolutely! Every AI model is tailored to your specific data, business objectives, and industry requirements. We work closely with your team to ensure optimal performance and integration.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              id="faq-item-4" 
              data-animate 
              className={getAnimationClass('faq-item-4', `bg-gray-900/30 rounded-2xl p-6 border transition-all duration-500 cursor-pointer ${
                expandedFAQ === 4 
                  ? 'border-green-200/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                  : 'border-gray-800 hover:border-gray-700'
              }`)}
              style={{ animationDelay: '0.4s' }}
              onClick={() => handleFAQClick(4)}
            >
              <button className="w-full flex items-center justify-between text-left">
                <span className="text-lg font-medium">How secure are your AI solutions?</span>
                <i className={`text-green-200 text-xl transition-transform duration-300 ${
                  expandedFAQ === 4 
                    ? 'ri-subtract-line rotate-0' 
                    : 'ri-arrow-right-up-line'
                }`}></i>
              </button>
              <div className={`transition-all duration-500 ease-out overflow-hidden ${
                expandedFAQ === 4 
                  ? 'max-h-96 opacity-100 mt-4' 
                  : 'max-h-0 opacity-0'
              }`}>
                <div className="border-t border-gray-700/50 pt-4">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    We implement enterprise‑grade security with end‑to‑end encryption, secure data processing, and compliance with GDPR, HIPAA, and SOC 2 standards. Your data never leaves your secure environment.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              id="faq-item-5" 
              data-animate 
              className={getAnimationClass('faq-item-5', `bg-gray-900/30 rounded-2xl p-6 border transition-all duration-500 cursor-pointer ${
                expandedFAQ === 5 
                  ? 'border-green-200/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                  : 'border-gray-800 hover:border-gray-700'
              }`)}
              style={{ animationDelay: '0.5s' }}
              onClick={() => handleFAQClick(5)}
            >
              <button className="w-full flex items-center justify-between text-left">
                <span className="text-lg font-medium">Do your AI products scale with business growth?</span>
                <i className={`text-green-200 text-xl transition-transform duration-300 ${
                  expandedFAQ === 5 
                    ? 'ri-subtract-line rotate-0' 
                    : 'ri-arrow-right-up-line'
                }`}></i>
              </button>
              <div className={`transition-all duration-500 ease-out overflow-hidden ${
                expandedFAQ === 5 
                  ? 'max-h-96 opacity-100 mt-4' 
                  : 'max-h-0 opacity-0'
              }`}>
                <div className="border-t border-gray-700/50 pt-4">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Yes, our AI solutions are built on cloud‑native architecture that automatically scales with your business needs. From startups to enterprises, our systems grow with you seamlessly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer 
        id="footer" 
        data-animate 
        className={getAnimationClass('footer', 'px-6 py-12 lg:px-12 border-t border-gray-800')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <a href="mailto:ai@nebula.com" className="text-4xl lg:text-5xl font-bold text-white hover:text-green-200 transition-colors cursor-pointer">
              ai@nebula.com
            </a>
          </div>

          <div className="border-t border-gray-800 pt-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="space-y-2">
                  <a href="mailto:ai@nebula.com" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                    ai@nebula.com
                  </a>
                  <a href="tel:+1234567890" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                    +1 (234) 567-8900
                  </a>
                </div>
              </div>

              <div className="space-y-2">
                <a href="#our-process" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  Process
                </a>
                <a href="#services" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  AI Products
                </a>
                <a href="#work" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  Projects
                </a>
                <a href="#testimonials" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  Testimonials
                </a>
                <a href="#contact" className="flex items-center text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  Contact
                  <i className="ri-arrow-right-up-line ml-1 text-xs"></i>
                </a>
              </div>

              <div className="space-y-2">
                <a href="https://www.linkedin.com/" target="_blank" rel="noopener" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  LinkedIn
                </a>
                <a href="https://www.x.com/" target="_blank" rel="noopener" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  Twitter
                </a>
                <a href="https://www.instagram.com/" target="_blank" rel="noopener" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  Instagram
                </a>
                <a href="https://www.dribbble.com/" target="_blank" rel="noopener" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  Dribbble
                </a>
                <div className="mt-4">
                  <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                    Powered by Readdy
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
